import _ from "lodash";
import React, { CSSProperties, useCallback, useEffect, useState } from "react";
import {
  FlatList,
  NativeSyntheticEvent,
  StyleProp,
  StyleSheet,
  Text,
  TextInput,
  TextInputFocusEventData,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { kAutoCompleteKeys } from "../custom-typings";
import { DropIcon } from "./impComponents";
import { SLImage } from "./ReactiveImage";

export type tOption<T = string> = {
  myLabel?: string;
  myValue: T;
  myImageSource?: string;
  isDefault?: boolean;
};

type tComboBoxProps<T extends string> = {
  myOptions: tOption<T>[];
  myPlaceholder?: string;
  disabled?: boolean;
  onSelect?: (myOption: tOption<T>, myIndex: number) => void;
  onSelectUnavailable?: (myOption: tOption<T>, myIndex: number) => void;
  value?: tOption<T>["myValue"];
  myAutocompleteType?: kAutoCompleteKeys;
  myNormalizeHandler?: (val: string) => string;
  fontStyle?: TextStyle;
  myDefaultIndex?: number;
  filterAvailable?: (myOption: tOption<T>) => boolean;
  showHighlight?: boolean;
  style?: StyleProp<ViewStyle>;
  textInputStyle?: StyleProp<TextStyle>;
};

const _handleFilterData = <T extends string>({ myData, myQuery }: { myData: Array<tOption<T>>; myQuery: string }) => {
  if (myQuery.length > 0) {
    const aData = _.cloneDeep(myData);
    return aData.filter(
      (myDatum) =>
        myDatum.myLabel?.toLowerCase().includes(myQuery.toLowerCase()) ||
        myDatum.myValue.toLowerCase().includes(myQuery.toLowerCase()),
    );
  }

  return myData;
};

const _highlight = (myString: string) => <Text style={styles.highlighted}>{myString}</Text>;

const getDefaultSelected = <T extends unknown>(
  options: tOption<T>[],
  value: T | undefined,
  defaultIndex: number | undefined,
): { value: tOption<T> | undefined; index: number } => {
  if (value === undefined) return { value: undefined, index: defaultIndex || -1 };
  const index = options.findIndex((option) => option.myValue === value);
  return { value: options[index], index };
};

const ComboBox = <T extends string>({
  myOptions,
  myPlaceholder,
  onSelect,
  onSelectUnavailable,
  disabled = false,
  value,
  myAutocompleteType,
  myNormalizeHandler,
  fontStyle,
  myDefaultIndex,
  filterAvailable,
  showHighlight = true,
  style,
  textInputStyle,
}: tComboBoxProps<T>): React.ReactElement => {
  const [sData, _setData] = useState<Array<tOption<T>>>(myOptions);
  const [sIsDropdownOpen, _setIsDropdownOpen] = useState<boolean>(false);
  const [sSelected, _setSelected] = useState<{ value: tOption<T> | undefined; index: number }>(
    getDefaultSelected(myOptions, value, myDefaultIndex),
  );
  const [sFilter, _setFilter] = useState<string>(
    sSelected.index > -1 ? myOptions[sSelected.index].myLabel || myOptions[sSelected.index].myValue : "",
  );

  const _keyExtractor = useCallback((item: tOption<T>) => item.myValue, []);

  const _handleSelect = useCallback(
    (myOption: tOption<T>) => {
      let aAvailable = true;
      if (filterAvailable !== undefined) {
        aAvailable = filterAvailable(myOption);
      }

      const aTempSelected = myOptions.find((val) => val.myValue === value);

      if (aAvailable && onSelect) {
        const aIndex = myOptions.findIndex((option) => option.myValue === myOption.myValue);
        onSelect(myOptions[aIndex], aIndex);
      } else if (!aAvailable && onSelectUnavailable) {
        const aIndex = myOptions.findIndex((option) => option.myValue === myOption.myValue);
        onSelectUnavailable(myOptions[aIndex], aIndex);
      }

      if (aAvailable || onSelectUnavailable) {
        _setSelected((prev) => ({ value: myOption, index: prev.index }));
        _setFilter(myOption.myLabel ?? myOption.myValue);
      } else {
        _setFilter(aTempSelected?.myLabel ?? aTempSelected?.myValue ?? "");
      }
    },
    [filterAvailable, myOptions, onSelect, onSelectUnavailable, value],
  );

  const _onPressRow = useCallback((myOption: tOption<T>) => () => _handleSelect(myOption), [_handleSelect]);

  const _handleFilter = useCallback(
    (myText: string) => {
      _setFilter(myText);

      const aNewData = _handleFilterData({
        myData: myOptions,
        myQuery: myText,
      });

      if (myOptions) {
        if (aNewData.length === 0) _setData(myOptions);
        else _setData(aNewData);
      }
    },
    [myOptions],
  );

  const _onFocusTextInput = useCallback(
    (myEvent: NativeSyntheticEvent<TextInputFocusEventData>) => {
      if (disabled) {
        myEvent.preventDefault();
      } else {
        _setData(myOptions);
        _setIsDropdownOpen(true);
      }
    },
    [myOptions, disabled],
  );

  const _onChangeText = useCallback(
    (myText) => {
      _handleFilter(myText);
    },
    [_handleFilter],
  );

  const _onBlurTextInput = useCallback(() => {
    if (!disabled) {
      if (sData.length === 1) {
        _handleSelect(sData[0]);
      } else if (myNormalizeHandler !== undefined) {
        const aOption = myOptions.find((myOption) => myOption.myValue === myNormalizeHandler(sFilter));
        if (aOption !== undefined) {
          _handleSelect(aOption);
        }
      } else if (sSelected.value !== undefined) {
        _setFilter(sSelected.value.myLabel ?? sSelected.value.myValue);
      }

      setTimeout(() => {
        _setIsDropdownOpen(false);
      }, 200);
    }
  }, [_handleSelect, disabled, myNormalizeHandler, myOptions, sData, sFilter, sSelected.value]);

  const _renderItem = useCallback(
    ({ item }: { item: tOption<T>; index: number }) => {
      const kImageProp = item.myImageSource;

      let aAvailable = true;
      if (filterAvailable !== undefined) {
        aAvailable = filterAvailable(item);
      }

      let aTextStyle = styles.comboBoxDropdownItemText;

      if (sSelected.value?.myValue === item.myValue) {
        aTextStyle = styles.comboBoxDropdownItemTextActive;
      } else if (!aAvailable) {
        aTextStyle = styles.comboBoxDropdownItemTextUnavailable;
      }

      const aDisplayText = item.myLabel ?? item.myValue;

      const aHighlightIndex = aDisplayText.toLowerCase().indexOf(sFilter.toLowerCase());

      const aStartText = aDisplayText.substring(0, aHighlightIndex);
      const aHighlightedText = aDisplayText.substr(aHighlightIndex, sFilter.length);
      const aEndText = aDisplayText.substring(aHighlightIndex + sFilter.length);

      return (
        <TouchableOpacity onPress={_onPressRow(item)}>
          <View
            style={
              sSelected.value?.myValue === item.myValue
                ? styles.comboBoxDropdownItemActive
                : styles.comboBoxDropdownItem
            }
          >
            {item.myImageSource && (
              <SLImage
                source={{
                  uri: kImageProp,
                }}
                mobileStyle={styles.comboBoxDropdownImage}
                webStyle={webStyles.comboBoxDropdownImage}
              />
            )}
            {!showHighlight || sFilter === "" || aHighlightIndex === -1 || item.isDefault ? (
              <Text style={[fontStyle, aTextStyle]}>{aDisplayText}</Text>
            ) : (
              <Text style={[fontStyle, aTextStyle]}>
                {aStartText}
                {aEndText !== undefined ? _highlight(aHighlightedText) : ""}
                {aEndText ?? ""}
              </Text>
            )}
          </View>
        </TouchableOpacity>
      );
    },
    [_onPressRow, filterAvailable, fontStyle, sFilter, sSelected.value?.myValue, showHighlight],
  );

  const _handleToggleDropdown = useCallback(() => {
    if (!disabled) _setIsDropdownOpen(!sIsDropdownOpen);
  }, [sIsDropdownOpen, disabled]);

  useEffect(() => {
    _setSelected({
      value: value ? { myValue: value } : undefined,
      index: myOptions.findIndex((val) => val.myValue === value),
    });
    _setFilter(
      myOptions.findIndex((val) => val.myValue === value) > -1
        ? myOptions[myOptions.findIndex((val) => val.myValue === value)]?.myLabel ||
            myOptions[myOptions.findIndex((val) => val.myValue === value)]?.myValue
        : myPlaceholder ?? "",
    );
  }, [myOptions, myPlaceholder, value]);

  useEffect(() => {
    myOptions.forEach((myOption, index) => {
      if (myOption.isDefault) {
        _setSelected({ value: myOption, index });
      }
    });
  }, [value, myOptions]);

  return (
    <View style={[styles.comboBox, style]}>
      {sSelected.value?.myImageSource !== undefined && (
        <SLImage
          source={{ uri: sSelected.value.myImageSource }}
          webStyle={webStyles.comboBoxDropdownImage}
          mobileStyle={styles.comboBoxDropdownImage}
        />
      )}
      <TextInput
        selectTextOnFocus={true}
        onFocus={_onFocusTextInput}
        value={sFilter}
        onChangeText={_onChangeText}
        onBlur={_onBlurTextInput}
        style={[fontStyle, disabled ? styles.comboBoxInputDisabled : styles.comboBoxInput, textInputStyle]}
        placeholder={myPlaceholder || ""}
        editable={!disabled}
        autoCompleteType={myAutocompleteType}
      />
      <View style={styles.gDropDownRow} accessible={true}>
        <Text style={[fontStyle, styles.gInvisible]}>{sFilter}</Text>
        <TouchableOpacity style={styles.iconRight} onPress={_handleToggleDropdown}>
          <DropIcon
            style={disabled ? styles.gDropDownIconDisabled : styles.gDropDownIcon}
            state={sIsDropdownOpen ? "up" : "down"}
          />
        </TouchableOpacity>
      </View>
      {sIsDropdownOpen && (
        <View style={styles.comboBoxDropdown}>
          <FlatList data={sData} keyExtractor={_keyExtractor} renderItem={_renderItem} extraData={sSelected.value} />
        </View>
      )}
    </View>
  );
};

export default ComboBox;

const styles = StyleSheet.create({
  comboBox: {
    alignItems: "center",
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    flexDirection: "row",
    height: 36,
    justifyContent: "space-between",
    paddingEnd: 10,
    paddingTop: 5,
  },
  comboBoxDropdown: {
    backgroundColor: kColors.white,
    borderBottomWidth: 1,
    borderColor: kColors.lightBlack,
    borderEndWidth: 1,
    borderStartWidth: 1,
    left: 0,
    maxHeight: 169,
    position: "absolute",
    shadowColor: kColors.black,
    shadowOffset: { width: 5, height: 5 },
    shadowOpacity: 0.1,
    shadowRadius: 10,
    top: 39,
    width: "100%",
    zIndex: 10,
  },
  comboBoxDropdownImage: {
    height: 70,
    width: 70,
  },
  comboBoxDropdownItem: {
    alignItems: "center",
    borderBottomColor: kColors.lightBlack,
    borderBottomWidth: 1,
    flexDirection: "row",
    zIndex: 20,
  },
  comboBoxDropdownItemActive: {
    alignItems: "center",
    backgroundColor: kColors.gray50,
    borderBottomColor: kColors.lightBlack,
    borderBottomWidth: 1,
    flexDirection: "row",
    ...kFonts.poppinsLight,
    color: kColors.brandDark,
    letterSpacing: 1,
  },
  comboBoxDropdownItemText: {
    ...kFonts.poppinsLight,
    color: kColors.brandDark,
    letterSpacing: 1,
    paddingHorizontal: 10,
    paddingVertical: 12,
  },
  comboBoxDropdownItemTextActive: {
    ...kFonts.poppinsLight,
    color: kColors.brandDark,
    letterSpacing: 1,
    paddingHorizontal: 10,
    paddingVertical: 12,
  },
  comboBoxDropdownItemTextUnavailable: {
    ...kFonts.poppinsLight,
    color: kColors.gray300,
    letterSpacing: 1,
    paddingHorizontal: 10,
    paddingVertical: 12,
  },
  comboBoxInput: {
    borderColor: kColors.transparent,
    borderWidth: 0,
    outlineColor: kColors.transparent,
    paddingHorizontal: kStyleConst.kSpacing * 2,
    paddingVertical: kStyleConst.kSpacing * 2,
    position: "absolute",
    width: "100%",
    zIndex: 2,
  },
  comboBoxInputDisabled: {
    borderColor: kColors.transparent,
    borderWidth: 0,
    color: kColors.lightMed,
    outlineColor: kColors.transparent,
    paddingHorizontal: kStyleConst.kSpacing * 2,
    paddingVertical: kStyleConst.kSpacing * 2,
    position: "absolute",
    width: "100%",
    zIndex: 2,
  },
  gDropDownIcon: {
    ...kStyleConst.kDropDownIcon,
  },
  gDropDownIconDisabled: {
    ...kStyleConst.kDropDownIcon,
    opacity: 0.25,
  },
  gDropDownRow: {
    flexDirection: "row",
    flex: 1,
    paddingVertical: kStyleConst.kSpacing * 2,
    zIndex: 0,
  },
  gInvisible: {
    color: kColors.transparent,
    paddingHorizontal: kStyleConst.kSpacing * 2,
    zIndex: 0,
  },
  highlighted: {
    fontWeight: "500",
  },
  iconRight: {
    alignItems: "flex-end",
    alignSelf: "center",
    flex: 1,
    height: 7,
    justifyContent: "center",
    zIndex: 3,
  },
});

const webStyles: { [key: string]: CSSProperties } = {
  comboBoxDropdownImage: {
    height: 70,
    // resizeMode: "cover",
    width: 70,
  },
} as const;
